import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Alert, Button, Modal } from 'react-bootstrap';

import { ExternalLink } from 'components/UI/ExternalLink';
import { billingDocumentHref, getBillingDocuments } from 'store/billing/service';

import { agreementDocumentLabelKey, useAgreementStatus } from './useAgreementStatus';

/**
 * The superseded case: a newer dated master is in force and the organization holds no acceptance
 * of it, so this is a re-acceptance rather than a confirmation and it does gate — but only the
 * Billing & Credits area, which is where it is mounted. The rest of the portal stays reachable.
 */
const AgreementReaffirmModal = () => {
  const intl = useIntl();
  const { applicable, superseded, saving, items, reaffirm } = useAgreementStatus();
  const [documents, setDocuments] = useState<IBillingDocument[]>([]);
  const [failed, setFailed] = useState(false);
  const show = applicable && superseded;

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    getBillingDocuments()
      .then((fetched) => {
        if (!cancelled) setDocuments(fetched);
      })
      // The masters are not registered yet, so a failure here only costs the links.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [show]);

  if (!show) return null;

  const handleAccept = async () => {
    setFailed(false);
    const succeeded = await reaffirm();
    if (!succeeded) setFailed(true);
  };

  return (
    <Modal show backdrop="static" keyboard={false} centered data-test="billing-agreement-modal">
      <Modal.Header>
        <Modal.Title data-test="billing-agreement-modal-title">
          {intl.formatMessage({ id: 'billingAgreementSupersededTitleKey' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p data-test="billing-agreement-modal-body">
          {intl.formatMessage({ id: 'billingAgreementSupersededBodyKey' })}
        </p>
        <ul className="mb-3 ps-3" data-test="billing-agreement-modal-documents">
          {items.map((item) => {
            const label = intl.formatMessage({ id: agreementDocumentLabelKey(item.documentType) });
            const url = documents.find((document) => document.type === item.documentType)?.url;
            return (
              <li
                key={item.documentType}
                data-test={`billing-agreement-document-${item.documentType}`}
              >
                {url ? (
                  <ExternalLink
                    href={billingDocumentHref(url)}
                    data-test={`billing-agreement-document-link-${item.documentType}`}
                  >
                    {label}
                  </ExternalLink>
                ) : (
                  label
                )}{' '}
                <span className="text-secondary">({item.identifier})</span>
              </li>
            );
          })}
        </ul>
        {failed && (
          <Alert variant="danger" className="mb-0" data-test="billing-agreement-modal-error">
            {intl.formatMessage({ id: 'billingAgreementErrorKey' })}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleAccept}
          disabled={saving}
          data-test="billing-agreement-modal-accept"
        >
          {intl.formatMessage({ id: 'billingAgreementAcceptKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AgreementReaffirmModal;
